import { Alert, App, Modal, Radio, Select, Space, Table, Tag, Typography } from "antd";
import { useEffect, useState } from "react";

import { fetchSiteCandidates, useSetEnabledBySite } from "../../api/candidates";
import { useNames } from "../../api/names";
import type { CandidateSummary } from "../../api/types";
import { useAuthenticated, usePermission } from "../../auth/SessionProvider";

/**
 * Close (or reopen) every account of one site, or of one room within it.
 * Candidates ticked in the list are excluded, which is how extended-time
 * candidates are kept open.
 */
export function SiteBatchModal({ open, initialSiteId, initialRoomId, onClose }: {
  open: boolean;
  initialSiteId?: number;
  initialRoomId?: number;
  onClose: () => void;
}) {
  const { token } = useAuthenticated();
  const { catalog, regionName, siteName, roomName } = useNames();
  const { message } = App.useApp();
  const mutation = useSetEnabledBySite();
  const canDisable = usePermission("candidate:disable");
  const canEnable = usePermission("candidate:enable");
  const [siteId, setSiteId] = useState<number | undefined>(initialSiteId);
  const [roomId, setRoomId] = useState<number | undefined>(initialRoomId);
  const [enabled, setEnabled] = useState(false);
  const [candidates, setCandidates] = useState<CandidateSummary[]>();
  const [loading, setLoading] = useState(false);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [error, setError] = useState<string>();
  const rooms = (catalog.data?.rooms ?? []).filter((room) => room.siteId === siteId);

  useEffect(() => {
    if (open) {
      setSiteId(initialSiteId);
      setRoomId(initialRoomId);
      setExcluded([]);
      setCandidates(undefined);
      setError(undefined);
    }
  }, [open, initialSiteId, initialRoomId]);

  useEffect(() => {
    if (!open || siteId === undefined) return;
    let cancelled = false;
    setLoading(true);
    fetchSiteCandidates(token, siteId, roomId)
      .then((items) => { if (!cancelled) setCandidates(items); })
      .catch((caught: unknown) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "加载失败"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, siteId, roomId, token]);

  const affected = (candidates ?? []).filter((item) => !excluded.includes(item.username) && item.enabled !== enabled);
  const scopeLabel = siteId === undefined ? "" : roomId === undefined ? `考点 ${siteName(siteId)}` : `考点 ${siteName(siteId)} 的考场 ${roomName(roomId)}`;

  const submit = async () => {
    if (siteId === undefined) return;
    try {
      const result = await mutation.mutateAsync({ siteId, roomId, enabled, exclude: excluded });
      message.success(`${enabled ? "开放" : "关闭"}了 ${result.updated} 个账号，排除 ${result.excluded.length} 个，${result.unchanged} 个状态未变`);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "操作失败");
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => void submit()}
      okText={`确认${enabled ? "开放" : "关闭"} ${affected.length} 个账号`}
      okButtonProps={{ danger: !enabled, disabled: siteId === undefined || affected.length === 0 || (enabled ? !canEnable : !canDisable) }}
      confirmLoading={mutation.isPending}
      title="按考点或考场批量操作"
      width={760}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space wrap>
          <Select<number>
            showSearch
            placeholder="选择考点"
            style={{ width: 320 }}
            value={siteId}
            onChange={(value) => { setSiteId(value); setRoomId(undefined); setExcluded([]); }}
            optionFilterProp="label"
            options={(catalog.data?.sites ?? []).map((site) => ({
              value: site.id,
              label: `${regionName(site.regionId)} / ${site.name}（${site.id}）`
            }))}
          />
          <Select<number>
            allowClear
            showSearch
            placeholder="整个考点"
            style={{ width: 200 }}
            value={roomId}
            disabled={siteId === undefined}
            onChange={(value) => { setRoomId(value); setExcluded([]); }}
            optionFilterProp="label"
            options={rooms.map((room) => ({ value: room.id, label: room.name }))}
          />
          <Radio.Group value={enabled} onChange={(event) => setEnabled(event.target.value as boolean)}>
            <Radio.Button value={false}>关闭账号</Radio.Button>
            <Radio.Button value={true}>开放账号</Radio.Button>
          </Radio.Group>
        </Space>
        {error && <Alert type="error" showIcon message={error} />}
        {siteId !== undefined && (
          <>
            <Typography.Text type="secondary">
              勾选需要排除的考生（例如延时考生），未勾选且状态需要改变的账号将被{enabled ? "开放" : "关闭"}。
            </Typography.Text>
            <Table<CandidateSummary>
              rowKey="username"
              size="small"
              loading={loading}
              dataSource={candidates ?? []}
              pagination={{ pageSize: 20, size: "small" }}
              rowSelection={{
                selectedRowKeys: excluded,
                onChange: (keys) => setExcluded(keys as string[]),
                columnTitle: "排除"
              }}
              columns={[
                { title: "准考证号", dataIndex: "username", sorter: (a, b) => a.username.localeCompare(b.username), defaultSortOrder: "ascend" },
                { title: "姓名", dataIndex: "name" },
                { title: "考场", dataIndex: "roomId", render: roomName },
                { title: "座位", dataIndex: "seat", render: (value: string | null) => value ?? "—" },
                { title: "状态", render: (_, row) => row.enabled ? <Tag color="green">启用</Tag> : <Tag color="red">已关闭</Tag> }
              ]}
            />
            <Typography.Text>
              {scopeLabel} 共 {candidates?.length ?? 0} 人，将{enabled ? "开放" : "关闭"} <strong>{affected.length}</strong> 个账号。
            </Typography.Text>
          </>
        )}
      </Space>
    </Modal>
  );
}
